/**
 * Reusable image uploader with drag-and-drop + file-browser fallback.
 *
 *   <ImageUploader
 *     value={form.image_url}
 *     onChange={(url) => setForm({...form, image_url: url})}
 *     kind="product"  // product | brand | supplier | letter | misc
 *   />
 *
 * Features:
 *   • Drag images onto the dropzone — or click to open the OS picker
 *   • Live preview before saving
 *   • Server-side optimisation to WebP at ≤2000px (handled by /api/uploads/image)
 *   • "Replace" / "Remove" actions on the preview
 *   • Falls back to the URL textbox for non-uploadable cases (eg. a CDN
 *     image the customer already hosts elsewhere). Setting the URL
 *     clears the uploaded blob — only ONE source of truth at a time.
 */
import React, { useCallback, useRef, useState } from 'react';
import { Upload, X, Image as ImageIcon, Loader2, Link as LinkIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import api from '../lib/api';

const ACCEPT = '.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp';
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB — matches backend cap

/**
 * Resolve a backend-relative URL (eg `/api/uploads/abc`) to a full URL the
 * browser can fetch. Absolute URLs are returned unchanged so externally
 * hosted images keep working.
 */
function resolveSrc(url) {
    if (!url) return '';
    if (/^https?:\/\//i.test(url) || url.startsWith('data:')) return url;
    // Use the same runtime-resolved API base so subdomain-scoped uploads
    // (e.g. `/api/uploads/abc.webp`) are fetched from the correct origin.
    const base =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'datacenter.universcosmetic.eu' ||
         window.location.hostname === 'portal.universcosmetic.eu')
            ? window.location.origin
            : (process.env.REACT_APP_BACKEND_URL || '');
    return `${base}${url}`;
}

export default function ImageUploader({
    value = '',
    onChange,
    kind = 'misc',
    placeholder = 'https://… or drop an image here',
    label,
    'data-testid': testId,
}) {
    const [busy, setBusy] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [urlInput, setUrlInput] = useState('');
    const inputRef = useRef(null);

    const sendFile = useCallback(async (file) => {
        if (!file) return;
        if (file.size > MAX_BYTES) {
            toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB) — max 10 MB.`);
            return;
        }
        if (!/^image\//.test(file.type)) {
            toast.error('Only image files are supported (JPG, PNG, WEBP).');
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('kind', kind);
            const { data } = await api.post('/uploads/image', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            onChange?.(data.url);
            toast.success('Image uploaded');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Upload failed');
        } finally {
            setBusy(false);
        }
    }, [kind, onChange]);

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) sendFile(file);
    };

    const handlePick = (e) => {
        const file = e.target.files?.[0];
        if (file) sendFile(file);
        e.target.value = '';
    };

    const commitUrl = () => {
        const v = urlInput.trim();
        if (!v) return;
        onChange?.(v);
        setUrlInput('');
    };

    const remove = () => onChange?.('');

    /* ----- Preview state ----- */
    if (value) {
        return (
            <div className="space-y-2" data-testid={testId}>
                {label && <div className="text-sm font-medium">{label}</div>}
                <div className="relative inline-block group">
                    <img
                        src={resolveSrc(value)}
                        alt="preview"
                        className="h-32 w-32 object-cover rounded-md border border-[#E5E5E5] bg-[#FBF8F6]"
                        onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                    />
                    <div className="absolute -top-2 -right-2 flex gap-1">
                        <Button
                            type="button" variant="secondary" size="icon"
                            className="h-7 w-7 shadow-md"
                            onClick={() => inputRef.current?.click()}
                            disabled={busy}
                            title="Replace"
                            data-testid={`${testId || 'img'}-replace`}
                        >
                            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                        </Button>
                        <Button
                            type="button" variant="destructive" size="icon"
                            className="h-7 w-7 shadow-md"
                            onClick={remove}
                            title="Remove"
                            data-testid={`${testId || 'img'}-remove`}
                        >
                            <X className="h-3 w-3" />
                        </Button>
                    </div>
                    <input
                        ref={inputRef}
                        type="file"
                        accept={ACCEPT}
                        onChange={handlePick}
                        className="hidden"
                    />
                </div>
                <div className="text-[11px] text-gray-400 break-all max-w-xs">{value}</div>
            </div>
        );
    }

    /* ----- Empty / dropzone state ----- */
    return (
        <div className="space-y-2" data-testid={testId}>
            {label && <div className="text-sm font-medium">{label}</div>}
            <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => inputRef.current?.click()}
                className={`cursor-pointer border-2 border-dashed rounded-md p-6 text-center transition
                    ${dragOver ? 'border-[#C08478] bg-[#FBF8F6]' : 'border-[#E5E5E5] hover:border-[#C08478] hover:bg-[#FBF8F6]/60'}`}
                role="button"
                tabIndex={0}
            >
                {busy ? (
                    <>
                        <Loader2 className="h-6 w-6 mx-auto text-[#C08478] animate-spin" />
                        <div className="text-xs text-gray-500 mt-2">Uploading…</div>
                    </>
                ) : (
                    <>
                        <ImageIcon className="h-6 w-6 mx-auto text-gray-400" />
                        <div className="text-xs text-gray-600 mt-2">
                            <span className="font-medium text-[#1A1A1A]">Click to upload</span> or drag & drop
                        </div>
                        <div className="text-[10px] text-gray-400 mt-1">JPG, PNG, WEBP · up to 10 MB</div>
                    </>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    accept={ACCEPT}
                    onChange={handlePick}
                    className="hidden"
                />
            </div>
            <div className="flex items-center gap-2">
                <LinkIcon className="h-3.5 w-3.5 text-gray-400" />
                <Input
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitUrl(); } }}
                    onBlur={commitUrl}
                    placeholder={placeholder}
                    className="text-xs h-8"
                    data-testid={`${testId || 'img'}-url`}
                />
            </div>
        </div>
    );
}
